import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Download, LogOut, RefreshCw } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;
const TOKEN_KEY = "rrw_admin_token";

export default function AdminLeads() {
  const [token, setToken] = useState(() => sessionStorage.getItem(TOKEN_KEY) || "");
  const [password, setPassword] = useState("");
  const [leads, setLeads] = useState([]);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const headers = { Authorization: `Bearer ${token}` };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/api/admin/leads`, { headers });
      setLeads(data);
      setError("");
    } catch {
      setToken("");
      sessionStorage.removeItem(TOKEN_KEY);
      setError("Session expired. Please log in again.");
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => {
    if (token) load();
  }, [token, load]);

  const login = async (e) => {
    e.preventDefault();
    setError("");
    try {
      const { data } = await axios.post(`${API}/api/admin/login`, { password });
      sessionStorage.setItem(TOKEN_KEY, data.token);
      setToken(data.token);
      setPassword("");
    } catch {
      setError("Invalid password.");
    }
  };

  const exportCsv = async () => {
    const res = await axios.get(`${API}/api/admin/leads/export`, { headers, responseType: "blob" });
    const url = URL.createObjectURL(res.data);
    const a = document.createElement("a");
    a.href = url;
    a.download = "rrw-leads.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  const logout = () => {
    sessionStorage.removeItem(TOKEN_KEY);
    setToken("");
    setLeads([]);
  };

  const fmt = (iso) =>
    new Date(iso).toLocaleString("en-US", {
      timeZone: "America/Phoenix",
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit"
    });

  if (!token) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-rest-base px-5 text-white" data-testid="admin-login">
        <form onSubmit={login} className="w-full max-w-sm border border-white/10 bg-rest-surface p-8">
          <img src="/logo.png" alt="Rest Recovery Wellness" className="mb-6 h-12 w-auto" />
          <h1 className="font-display text-2xl font-black uppercase tracking-tighter">Lead Dashboard</h1>
          <p className="mt-2 text-sm text-zinc-500">Private. Authorised staff only.</p>
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Admin password"
            data-testid="admin-login-password"
            className="mt-6 w-full border border-white/15 bg-rest-base px-4 py-3 text-sm text-white outline-none placeholder:text-zinc-600 focus:border-rest-red"
          />
          {error && <p className="mt-3 text-sm text-rest-red" data-testid="admin-login-error">{error}</p>}
          <button
            type="submit"
            data-testid="admin-login-submit"
            className="mt-5 w-full bg-rest-red px-6 py-3 font-mono text-xs uppercase tracking-[0.2em] text-white transition-colors duration-300 hover:bg-[#ff4d5a]"
          >
            Log in
          </button>
        </form>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-rest-base px-5 py-10 text-white md:px-10" data-testid="admin-dashboard">
      <div className="mx-auto max-w-6xl">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <img src="/logo.png" alt="Rest Recovery Wellness" className="h-10 w-auto" />
            <div>
              <h1 className="font-display text-2xl font-black uppercase tracking-tighter">Recovery Experience Leads</h1>
              <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">
                {leads.length} lead{leads.length === 1 ? "" : "s"} captured
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={load}
              data-testid="admin-refresh-button"
              className="inline-flex items-center gap-2 border border-white/15 px-4 py-2.5 font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-300 transition-colors duration-300 hover:border-white/50"
            >
              <RefreshCw className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} /> Refresh
            </button>
            <button
              onClick={exportCsv}
              data-testid="admin-export-csv-button"
              className="inline-flex items-center gap-2 bg-rest-red px-4 py-2.5 font-mono text-[10px] uppercase tracking-[0.2em] text-white transition-colors duration-300 hover:bg-[#ff4d5a]"
            >
              <Download className="h-3.5 w-3.5" /> Export CSV
            </button>
            <button
              onClick={logout}
              data-testid="admin-logout-button"
              className="inline-flex items-center gap-2 border border-white/15 px-4 py-2.5 font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-300 transition-colors duration-300 hover:border-white/50"
            >
              <LogOut className="h-3.5 w-3.5" /> Log out
            </button>
          </div>
        </div>

        {error && <p className="mt-6 text-sm text-rest-red">{error}</p>}

        <div className="mt-8 overflow-x-auto border border-white/10">
          <table className="w-full min-w-[760px] text-left text-sm" data-testid="admin-leads-table">
            <thead>
              <tr className="border-b border-white/10 bg-rest-surface font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">
                <th className="px-4 py-3">First Name</th>
                <th className="px-4 py-3">Mobile Number</th>
                <th className="px-4 py-3">Email</th>
                <th className="px-4 py-3">Primary Goal</th>
                <th className="px-4 py-3">Lead Source</th>
                <th className="px-4 py-3">Submitted</th>
              </tr>
            </thead>
            <tbody>
              {leads.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-4 py-10 text-center text-zinc-500" data-testid="admin-leads-empty">
                    No leads yet. They'll appear here the moment someone claims their free session.
                  </td>
                </tr>
              )}
              {leads.map((l) => (
                <tr key={l.id} className="border-b border-white/5 transition-colors duration-200 hover:bg-rest-surface" data-testid={`admin-lead-row-${l.id}`}>
                  <td className="px-4 py-3 font-semibold">{l.first_name}</td>
                  <td className="px-4 py-3">
                    <a href={`tel:${l.mobile}`} className="text-zinc-300 hover:text-white">{l.mobile}</a>
                  </td>
                  <td className="px-4 py-3">
                    <a href={`mailto:${l.email}`} className="text-zinc-300 hover:text-white">{l.email}</a>
                  </td>
                  <td className="px-4 py-3 text-zinc-300">{l.primary_goal}</td>
                  <td className="px-4 py-3">
                    <span className={`font-mono text-[10px] uppercase tracking-[0.15em] ${l.lead_source === "google_ads" ? "text-rest-blue" : "text-zinc-500"}`}>
                      {l.lead_source}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-zinc-400">{fmt(l.created_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="mt-4 font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-600">
          Times shown in Phoenix time. This page is not linked from the public site.
        </p>
      </div>
    </div>
  );
}
